import React, { useState, useCallback, useEffect, useRef } from 'react';
import { GAME_REGISTRY } from '../../games/models/GameRegistry';
import { gameAPI, SavedSong as FirebaseSavedSong } from '../../games/services/GameAPI';
import { auth, db } from '../../../shared/storage/firebase';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import { useAuth } from '../../auth/contexts/AuthContext';
import SongNamingModal from '../../games/components/SongNamingModal';
import '../styles/SavedSongs.css';
import defaultGameImage from '../../../shared/assets/images/games/default-game.png';

interface SongStats {
  totalSongs: number;
  mostPlayedGame: string;
  favoriteSong: string;
}

interface TopTenSong {
  id: string;
  song: FirebaseSavedSong;
  rank: number;
}

type SeasonName = 'winter' | 'spring' | 'summer' | 'fall';

const SEASON_ORDER: Record<SeasonName, number> = {
  winter: 0,
  spring: 1,
  summer: 2,
  fall: 3,
};

const SEASON_META: Record<SeasonName, { label: string; emoji: string }> = {
  winter: { label: 'Winter', emoji: '❄️' },
  spring: { label: 'Spring', emoji: '🌸' },
  summer: { label: 'Summer', emoji: '☀️' },
  fall: { label: 'Fall', emoji: '🍁' },
};

const getSeasonFromDate = (date: Date): SeasonName => {
  try {
    const month = date.getMonth() + 1;
    if (month >= 1 && month <= 3) return 'winter';
    if (month >= 4 && month <= 6) return 'spring';
    if (month >= 7 && month <= 8) return 'summer';
    return 'fall';
  } catch {
    return 'fall';
  }
};

const getAlbumKeyFromDate = (date: Date): string => {
  const season = getSeasonFromDate(date);
  const year = date.getFullYear();
  return `${year}-${season}`;
};

const parseAlbumKey = (albumKey: string): { year: number; season: SeasonName } => {
  const [yearRaw, seasonRaw] = albumKey.split('-');
  const year = Number(yearRaw) || new Date().getFullYear();
  const season = (seasonRaw as SeasonName) || 'fall';
  return { year, season };
};

const compareAlbumKeys = (a: string, b: string): number => {
  const parsedA = parseAlbumKey(a);
  const parsedB = parseAlbumKey(b);
  if (parsedA.year !== parsedB.year) return parsedA.year - parsedB.year;
  return SEASON_ORDER[parsedA.season] - SEASON_ORDER[parsedB.season];
};

const SavedSongs: React.FC = () => {
  const { userData } = useAuth();
  const [currentlyPlaying, setCurrentlyPlaying] = useState<string | null>(null);
  const [realSongs, setRealSongs] = useState<FirebaseSavedSong[]>([]);
  const [loading, setLoading] = useState(true);
  const [currentAudio, setCurrentAudio] = useState<HTMLAudioElement | null>(null);
  const [editingSong, setEditingSong] = useState<string | null>(null);
  const [editingTitle, setEditingTitle] = useState<string>('');
  const [savingRename, setSavingRename] = useState<string | null>(null);
  const [topTenSongs, setTopTenSongs] = useState<TopTenSong[]>([]);
  const [draggedSong, setDraggedSong] = useState<FirebaseSavedSong | null>(null);
  const [currentSongIndex, setCurrentSongIndex] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [volume, setVolume] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState<'date' | 'title' | 'game'>('date');
  const [playQueueType, setPlayQueueType] = useState<'album' | 'top10'>('album');
  const [userName, setUserName] = useState<string>('');
  const [editingChartSong, setEditingChartSong] = useState<string | null>(null);
  const [editingChartTitle, setEditingChartTitle] = useState<string>('');
  const [activeAlbum, setActiveAlbum] = useState<string>(() => getAlbumKeyFromDate(new Date()));
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const albumTabsRef = useRef<HTMLDivElement | null>(null);

  // Helper function to check if song is new (created in past 2 weeks)
  const isNewSong = (createdAt: Date): boolean => {
    const twoWeeksAgo = new Date();
    twoWeeksAgo.setDate(twoWeeksAgo.getDate() - 14);
    return createdAt > twoWeeksAgo;
  };

  const currentAlbumKey = getAlbumKeyFromDate(new Date());

  // Fetch real user songs from Firebase
  useEffect(() => {
    const fetchUserSongs = async () => {
      try {
        setLoading(true);
        const uid = auth.currentUser?.uid;
        if (uid) {
          console.log('🎵 Fetching user songs from Firebase...');
          const userSongs = await gameAPI.getUserSongs();
          console.log('📊 Found user songs:', userSongs);
          setRealSongs(userSongs);

          // Load persisted Top 10 from Firestore
          try {
            const chartRef = doc(db, 'users', uid, 'topCharts', 'top10');
            const chartSnap = await getDoc(chartRef);
            if (chartSnap.exists()) {
              const data = chartSnap.data() as any;
              const items = Array.isArray(data.items) ? data.items : [];
              const mapped: TopTenSong[] = items
                .filter((it: any) => it && typeof it.songId === 'string' && typeof it.rank === 'number')
                .map((it: any) => {
                  const song = userSongs.find(s => s.id === it.songId);
                  return song ? { id: `top10-${it.songId}`, song, rank: it.rank } : null;
                })
                .filter(Boolean) as TopTenSong[];
              mapped.sort((a, b) => a.rank - b.rank);
              setTopTenSongs(mapped);
            }
          } catch (e) {
            console.warn('⚠️ Failed to load Top 10 chart, proceeding without it', e);
          }

          // Get user name
          const user = auth.currentUser;
          if (user?.displayName) {
            setUserName(user.displayName);
          } else if (user?.email) {
            setUserName(user.email.split('@')[0]);
          } else {
            setUserName('Student');
          }
        }
      } catch (error) {
        console.error('❌ Error fetching user songs:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchUserSongs();
  }, []);

  // Handle song rename
  const handleRename = useCallback(async (songId: string, newTitle: string) => {
    if (!newTitle.trim() || savingRename) return;

    try {
      setSavingRename(songId);
      console.log(`✏️ Renaming song ${songId} to "${newTitle}"`);
      
      await gameAPI.updateSong(songId, { title: newTitle.trim() });
      
      // Update local state
      setRealSongs(prev => prev.map(song => 
        song.id === songId ? { ...song, title: newTitle.trim() } : song
      ));
      
      setEditingSong(null);
      setEditingTitle('');
      console.log(`✅ Song renamed successfully`);
      
    } catch (error) {
      console.error('❌ Error renaming song:', error);
      void window.appAlert('Failed to rename song. Please try again.');
    } finally {
      setSavingRename(null);
    }
  }, [savingRename]);

  const handleDeleteSong = useCallback(async (song: FirebaseSavedSong) => {
    if (!song.id) return;
    const confirmed = await window.appConfirm(`Delete "${song.title}"? This cannot be undone.`);
    if (!confirmed) return;

    try {
      await gameAPI.deleteSong(song.id);

      // Remove from local song list and chart list
      setRealSongs(prev => prev.filter(item => item.id !== song.id));
      setTopTenSongs(prev => {
        const next = prev
          .filter(item => item.song.id !== song.id)
          .map((item, index) => ({ ...item, rank: index + 1 }));

        const uid = auth.currentUser?.uid;
        if (uid) {
          const items = next.map(item => ({ songId: item.song.id!, rank: item.rank }));
          const chartRef = doc(db, 'users', uid, 'topCharts', 'top10');
          setDoc(chartRef, { items, updatedAt: new Date() }, { merge: true }).catch(err => {
            console.error('❌ Failed to save Top 10 chart after deletion:', err);
          });
        }

        return next;
      });

      // Stop player if deleted song was active
      if (currentlyPlaying === song.id) {
        currentAudio?.pause();
        setCurrentAudio(null);
        setCurrentlyPlaying(null);
        setIsPlaying(false);
        setCurrentTime(0);
        setDuration(0);
      }
    } catch (error) {
      console.error('❌ Error deleting song:', error);
      void window.appAlert('Failed to delete song. Please try again.');
    }
  }, [currentlyPlaying, currentAudio]);

  const startEditing = useCallback((songId: string, currentTitle: string) => {
    // Ensure we don't show multiple rename UIs at once
    setEditingChartSong(null);
    setEditingChartTitle('');
    setEditingSong(songId);
    setEditingTitle(currentTitle);
  }, []);

  const cancelEditing = useCallback(() => {
    setEditingSong(null);
    setEditingTitle('');
  }, []);

  // Convert Firebase song to display format
  const getGameDataForSong = (song: FirebaseSavedSong) => {
    // Normalize gameId to handle variations (e.g., "PianoClicker" -> "piano-clicker")
    let normalizedGameId = song.gameId;
    
    // Handle PianoClicker variations
    if (song.gameId && /^pianoclicker/i.test(song.gameId)) {
      normalizedGameId = 'piano-clicker';
    }
    
    // Handle Harmonidome variations (Harmonidome -> harmonidome)
    if (song.gameId && /^harmonidome$/i.test(song.gameId)) {
      normalizedGameId = 'harmonidome';
    }
    
    let game = GAME_REGISTRY[normalizedGameId];
    
    // Fallback: If game not found or is unknown, try to detect from song title
    if (!game || normalizedGameId === 'unknown') {
      const title = song.title?.toLowerCase() || '';
      
      // Check for game names in the title
      if (/piano\s*clicker/i.test(title)) {
        game = GAME_REGISTRY['piano-clicker'];
      } else if (/harmonidome/i.test(title)) {
        game = GAME_REGISTRY['harmonidome'];
      } else if (/beat\s*decoder/i.test(title)) {
        game = GAME_REGISTRY['beat-decoders'];
      } else if (/rhythm\s*rally/i.test(title)) {
        game = GAME_REGISTRY['rhythm-rally'];
      } else if (/melopede/i.test(title)) {
        game = GAME_REGISTRY['melopedes'];
      }
    }
    
    return {
      title: game?.title || 'Unknown Game',
      thumbnail: game?.thumbnail || defaultGameImage,
      musicalSkills: game?.musicalSkills || [],
      elements: game?.musicalElements || []
    };
  };

  const albumKeys = Array.from(
    new Set([...realSongs.map(song => getAlbumKeyFromDate(song.createdAt)), currentAlbumKey])
  ).sort(compareAlbumKeys);

  useEffect(() => {
    if (!albumKeys.includes(activeAlbum)) {
      setActiveAlbum(currentAlbumKey);
    }
  }, [albumKeys, activeAlbum, currentAlbumKey]);

  useEffect(() => {
    if (!albumTabsRef.current) return;
    const activeEl = albumTabsRef.current.querySelector(`[data-album-key="${activeAlbum}"]`) as HTMLElement | null;
    if (activeEl) {
      activeEl.scrollIntoView({ behavior: 'smooth', inline: 'end', block: 'nearest' });
    }
  }, [activeAlbum, albumKeys.length]);

  const albumSongs = realSongs.filter(song => getAlbumKeyFromDate(song.createdAt) === activeAlbum);

  // Filter and sort songs for the active album
  const filteredSongs = albumSongs.filter(song =>
    song.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
    getGameDataForSong(song).title.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const sortedSongs = [...filteredSongs].sort((a, b) => {
    switch (sortBy) {
      case 'title':
        return a.title.localeCompare(b.title);
      case 'game':
        return getGameDataForSong(a).title.localeCompare(getGameDataForSong(b).title);
      case 'date':
      default:
        return b.createdAt.getTime() - a.createdAt.getTime();
    }
  });
  const sortedTopTenSongs = [...topTenSongs]
    .sort((a, b) => a.rank - b.rank)
    .map((item) => item.song);

  // Calculate stats for real songs only
  const totalSongs = realSongs.length;
  const stats: SongStats = {
    totalSongs,
    mostPlayedGame: realSongs.length > 0 ?
      (GAME_REGISTRY[realSongs[0].gameId]?.title || 'Unknown Game') :
      'No games yet',
    favoriteSong: realSongs.length > 0 ? realSongs[0].title :
      'No songs yet',
  };

  // Play real audio file
  const playRealAudio = useCallback(async (song: FirebaseSavedSong) => {
    try {
      // Stop any currently playing audio
      if (currentAudio) {
        currentAudio.pause();
        currentAudio.currentTime = 0;
        setCurrentAudio(null);
      }

      if (!song.audioUrl) {
        console.warn('No audio URL found for song:', song.title);
        return;
      }

      setCurrentlyPlaying(song.id || song.title);
      setIsPlaying(true);
      console.log('🎵 Playing audio from URL:', song.audioUrl);

      const audio = new Audio(song.audioUrl);
      setCurrentAudio(audio);
      audioRef.current = audio;

      // Set volume
      audio.volume = volume;

      // Audio event listeners
      audio.onloadedmetadata = () => {
        setDuration(audio.duration);
      };

      audio.ontimeupdate = () => {
        setCurrentTime(audio.currentTime);
      };

      audio.onended = () => {
        setCurrentlyPlaying(null);
        setCurrentAudio(null);
        setIsPlaying(false);
        setCurrentTime(0);

        // Auto-play next song from the active queue context.
        if (playQueueType === 'top10') {
          const topTenIndex = sortedTopTenSongs.findIndex((s) => s.id === song.id);
          const nextTopTenIndex = topTenIndex >= 0 && topTenIndex < sortedTopTenSongs.length - 1 ? topTenIndex + 1 : 0;
          if (sortedTopTenSongs[nextTopTenIndex]) {
            setTimeout(() => playRealAudio(sortedTopTenSongs[nextTopTenIndex]), 500);
          }
          return;
        }

        const nextIndex = currentSongIndex < sortedSongs.length - 1 ? currentSongIndex + 1 : 0;
        if (sortedSongs[nextIndex]) {
          setCurrentSongIndex(nextIndex);
          setTimeout(() => playRealAudio(sortedSongs[nextIndex]), 500);
        }
      };

      audio.onerror = (error) => {
        console.error('❌ Error playing audio:', error);
        setCurrentlyPlaying(null);
        setCurrentAudio(null);
        setIsPlaying(false);
        setCurrentTime(0);
      };

      await audio.play();
    } catch (error) {
      console.error('❌ Error playing real audio:', error);
      setCurrentlyPlaying(null);
      setCurrentAudio(null);
      setIsPlaying(false);
      setCurrentTime(0);
    }
  }, [currentAudio, volume, currentSongIndex, sortedSongs, playQueueType, sortedTopTenSongs]);


  const handleRealSongPlay = (e: React.MouseEvent, song: FirebaseSavedSong) => {
    e.stopPropagation();
    setPlayQueueType('album');
    const songIndexInAlbum = sortedSongs.findIndex((s) => s.id === song.id);
    if (songIndexInAlbum >= 0) {
      setCurrentSongIndex(songIndexInAlbum);
    }
    playRealAudio(song);
  };

  const handleChartSongPlay = useCallback((e: React.MouseEvent, song: FirebaseSavedSong) => {
    e.stopPropagation();

    if (isPlaying && currentlyPlaying === song.id) {
      currentAudio?.pause();
      setIsPlaying(false);
      setCurrentlyPlaying(null);
      return;
    }

    const chartSongIndexInAlbum = sortedSongs.findIndex((s) => s.id === song.id);
    if (chartSongIndexInAlbum >= 0) {
      setCurrentSongIndex(chartSongIndexInAlbum);
    }
    setPlayQueueType('top10');
    playRealAudio(song);
  }, [isPlaying, currentlyPlaying, currentAudio, sortedSongs, playRealAudio]);


  // Stop audio when component unmounts
  useEffect(() => {
    return () => {
      if (currentAudio) {
        currentAudio.pause();
        setCurrentAudio(null);
      }
    };
  }, [currentAudio]);


  // Drag and drop handlers
  const handleDragStart = (e: React.DragEvent, song: FirebaseSavedSong) => {
    setDraggedSong(song);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDropOnChart = (e: React.DragEvent, targetRank: number) => {
    e.preventDefault();
    if (!draggedSong) return;

    const newTopTenSongs = [...topTenSongs];
    const existingIndex = newTopTenSongs.findIndex(item => item.song.id === draggedSong.id);

    if (existingIndex !== -1) {
      // Reordering within top 10
      const [removed] = newTopTenSongs.splice(existingIndex, 1);
      newTopTenSongs.splice(targetRank - 1, 0, { ...removed, rank: targetRank });
    } else {
      // Adding new song to top 10
      if (newTopTenSongs.length >= 10) {
        newTopTenSongs.pop(); // Remove last item if at capacity
      }
      newTopTenSongs.splice(targetRank - 1, 0, {
        id: `top10-${Date.now()}`,
        song: draggedSong,
        rank: targetRank
      });
    }

    // Update ranks
    newTopTenSongs.forEach((item, index) => {
      item.rank = index + 1;
    });

    setTopTenSongs(newTopTenSongs);
    setDraggedSong(null);

    // Persist updated Top 10 to Firestore
    const uid = auth.currentUser?.uid;
    if (uid) {
      const items = newTopTenSongs.map(item => ({ songId: item.song.id!, rank: item.rank }));
      const chartRef = doc(db, 'users', uid, 'topCharts', 'top10');
      setDoc(chartRef, { items, updatedAt: new Date() }, { merge: true }).catch(err => {
        console.error('❌ Failed to save Top 10 chart:', err);
      });
    }
  };

  // Chart song renaming functions
  const startEditingChart = useCallback((songId: string, currentTitle: string) => {
    // Ensure we don't show multiple rename UIs at once
    setEditingSong(null);
    setEditingTitle('');
    setEditingChartSong(songId);
    setEditingChartTitle(currentTitle);
  }, []);

  const cancelEditingChart = useCallback(() => {
    setEditingChartSong(null);
    setEditingChartTitle('');
  }, []);

  const handleChartRename = useCallback(async (songId: string, newTitle: string) => {
    if (!newTitle.trim()) return;

    try {
      await gameAPI.updateSong(songId, { title: newTitle.trim() });

      // Update local state
      setRealSongs(prev => prev.map(song =>
        song.id === songId ? { ...song, title: newTitle.trim() } : song
      ));

      // Update top ten songs
      setTopTenSongs(prev => prev.map(item =>
        item.song.id === songId ? { ...item, song: { ...item.song, title: newTitle.trim() } } : item
      ));

      setEditingChartSong(null);
      setEditingChartTitle('');
    } catch (error) {
      console.error('❌ Error renaming song:', error);
      void window.appAlert('Failed to rename song. Please try again.');
    }
  }, []);

  const handleDragEnd = () => {
    setDraggedSong(null);
  };

  const downloadSong = async (song: FirebaseSavedSong) => {
    if (!song.audioUrl) {
      console.warn('No audio URL available for download');
      return;
    }

    try {
      console.log('📥 Downloading song:', song.title);
      
      // Fetch the audio file
      const response = await fetch(song.audioUrl);
      const blob = await response.blob();
      
      // Create a download link
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      
      // Generate filename: sanitize song title and add .wav extension
      const sanitizedTitle = song.title.replace(/[^a-z0-9]/gi, '_').toLowerCase();
      link.download = `${sanitizedTitle}_${song.id}.wav`;
      
      // Trigger download
      document.body.appendChild(link);
      link.click();
      
      // Cleanup
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      console.log('✅ Song downloaded successfully');
    } catch (error) {
      console.error('❌ Error downloading song:', error);
      void window.appAlert('Failed to download song. Please try again.');
    }
  };

  // Get current song for hero display
  const currentSong = sortedSongs[currentSongIndex] || null;
  const currentGameData = currentSong ? getGameDataForSong(currentSong) : null;

  // Ensure currentSongIndex stays in range when album or filters change
  useEffect(() => {
    if (sortedSongs.length === 0) {
      if (currentSongIndex !== 0) {
        setCurrentSongIndex(0);
      }
      return;
    }

    if (currentSongIndex >= sortedSongs.length) {
      setCurrentSongIndex(0);
    }
  }, [sortedSongs.length, currentSongIndex]);

  return (
    <div className="music-streaming-container">
      {/* Kid-friendly rename modal (includes emoji buttons) */}
      {editingSong && (
        <SongNamingModal
          defaultName={editingTitle}
          onSkip={cancelEditing}
          onSave={(name) => handleRename(editingSong, name)}
        />
      )}
      {editingChartSong && (
        <SongNamingModal
          defaultName={editingChartTitle}
          onSkip={cancelEditingChart}
          onSave={(name) => handleChartRename(editingChartSong, name)}
        />
      )}

      {/* Hero Section */}
      <div className="hero-section">
        {currentSong && currentGameData && (
          <>
            <div className="hero-background">
              <img
                src={currentGameData.thumbnail}
                alt={currentGameData.title}
                className="hero-image"
              />
              <div className="hero-overlay"></div>
            </div>
            <div className="hero-content">
              <div className="hero-album-art">
                <img
                  src={currentGameData.thumbnail}
                  alt={currentGameData.title}
                  className="hero-album-image"
                />
                <button
                  className="hero-play-button"
                  onClick={() => {
                    setPlayQueueType('album');
                    playRealAudio(currentSong);
                  }}
                >
                  {isPlaying && currentlyPlaying === currentSong.id ? '⏸️' : '▶️'}
                </button>
              </div>
              <div className="hero-info">
                <h1
                  className="hero-song-title editable"
                  onClick={() => startEditing(currentSong.id!, currentSong.title)}
                  title="Click to rename this song"
                >
                  {currentSong.title}
                </h1>
                <p className="hero-artist">{userName}</p>
                <p className="hero-details">
                  {currentGameData.title} • Created {currentSong.createdAt.toLocaleDateString()} • {stats.totalSongs} songs
                </p>
              </div>
            </div>
          </>
        )}
        {!currentSong && (
          <div className="hero-empty">
            <h1 className="hero-title">🎵 Your Music Library</h1>
            <p className="hero-subtitle">Create songs in games to build your collection</p>
          </div>
        )}
      </div>

      {/* Main Content */}
      <div className="main-content">
        {/* Left Panel - Your Creations */}
        <div className="creations-panel">
          <div className="panel-header">
            <div className="album-tabs-wrapper">
              <div className="album-tabs" ref={albumTabsRef}>
                {albumKeys.map((albumKey) => {
                  const { year, season } = parseAlbumKey(albumKey);
                  const seasonMeta = SEASON_META[season];
                  const isCurrent = albumKey === currentAlbumKey;
                  return (
                    <button
                      key={albumKey}
                      data-album-key={albumKey}
                      className={`album-tab ${activeAlbum === albumKey ? 'active' : ''} ${isCurrent ? 'current-season' : ''}`}
                      onClick={() => setActiveAlbum(albumKey)}
                    >
                      {seasonMeta.emoji} {seasonMeta.label} {year}
                    </button>
                  );
                })}
              </div>
            </div>
            <div className="panel-stats">
              {albumSongs.length} {albumSongs.length === 1 ? 'song' : 'songs'}
            </div>
          </div>

          <div className="panel-controls">
            <div className="search-bar">
              <input
                type="text"
                placeholder="Search your music..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="search-input"
              />
            </div>
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value as 'date' | 'title' | 'game')}
              className="sort-select"
            >
              <option value="date">Date Created</option>
              <option value="title">Song Title</option>
              <option value="game">Game</option>
            </select>
          </div>

          {loading && (
            <div className="loading-state">
              <div className="loading-spinner"></div>
              <p>Loading your music...</p>
            </div>
          )}

          <div className="songs-list">
            {sortedSongs.map((song, index) => {
              const gameData = getGameDataForSong(song);
              const isCurrentlyPlaying = currentlyPlaying === song.id;

              return (
                <div
                  key={song.id || index}
                  className={`song-row ${isCurrentlyPlaying ? 'playing' : ''} ${index === currentSongIndex ? 'selected' : ''}`}
                  draggable
                  onDragStart={(e) => handleDragStart(e, song)}
                  onDragEnd={handleDragEnd}
                  onClick={() => setCurrentSongIndex(index)}
                >
                  <div className="song-number">{index + 1}</div>
                  <div className="song-album-art">
                    <img src={gameData.thumbnail} alt={gameData.title} />
                    <button
                      className="song-play-btn"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleRealSongPlay(e, song);
                      }}
                    >
                      {isCurrentlyPlaying ? '⏸️' : '▶️'}
                    </button>
                  </div>
                  <div className="song-info">
                    <div className="song-title-container">
                      <div
                        className="song-title"
                        onClick={(e) => {
                          e.stopPropagation();
                          startEditing(song.id!, song.title);
                        }}
                        onMouseDown={(e) => e.stopPropagation()}
                      >
                        {song.title}
                        {isNewSong(song.createdAt) && <span className="new-badge">NEW</span>}
                      </div>
                    </div>
                    <div className="song-meta-row">
                      <div className="song-submeta">
                        <div className="song-artist">{userName}</div>
                        <div className="song-date">{song.createdAt.toLocaleDateString()}</div>
                      </div>
                      <div className="song-actions">
                        <button
                          className="action-btn"
                          onClick={(e) => {
                            e.stopPropagation();
                            downloadSong(song);
                          }}
                          title="Download song"
                        >
                          ⬇️
                        </button>
                        <button
                          className="action-btn"
                          onClick={(e) => {
                            e.stopPropagation();
                            startEditing(song.id!, song.title);
                          }}
                        >
                          ✏️
                        </button>
                        <button
                          className="action-btn delete-btn"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDeleteSong(song);
                          }}
                          title="Delete song"
                        >
                          🗑️
                        </button>
                        <div className="drag-handle">⋮⋮</div>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {!loading && sortedSongs.length === 0 && (
            <div className="empty-state">
              <div className="empty-icon">🎵</div>
              <h3>No songs found</h3>
              <p>Create music in games to build your library</p>
            </div>
          )}
        </div>

        {/* Right Panel - Top 10 Chart */}
        <div className="top10-panel">
          <div className="panel-header">
            <h2 className="panel-title">🏆 Top 10 Chart</h2>
            <div className="panel-subtitle">Drag songs here to rank them</div>
          </div>

          <div className="chart-list">
            {Array.from({ length: 10 }, (_, index) => {
              const rank = index + 1;
              const chartSong = topTenSongs.find(item => item.rank === rank);
              const isChartSongPlaying = !!chartSong?.song?.id && currentlyPlaying === chartSong.song.id && isPlaying;

              return (
                <div
                  key={rank}
                  className={`chart-row ${chartSong ? 'filled' : 'empty'}`}
                  onDragOver={handleDragOver}
                  onDrop={(e) => handleDropOnChart(e, rank)}
                >
                  <div className="chart-rank">{rank}</div>
                  {chartSong ? (
                    <>
                      <div className="chart-album-art">
                        <img
                          src={getGameDataForSong(chartSong.song).thumbnail}
                          alt={getGameDataForSong(chartSong.song).title}
                        />
                        <button
                          className={`chart-play-btn ${isChartSongPlaying ? 'playing' : ''}`}
                          onClick={(e) => handleChartSongPlay(e, chartSong.song)}
                          title={isChartSongPlaying ? "Pause song" : "Play song"}
                        >
                          {isChartSongPlaying ? '⏸️' : '▶️'}
                        </button>
                      </div>
                      <div className="chart-info">
                        <div className="chart-title-container">
                          <div
                            className="chart-title"
                            onClick={(e) => {
                              e.stopPropagation();
                              startEditingChart(chartSong.song.id!, chartSong.song.title);
                            }}
                            onMouseDown={(e) => e.stopPropagation()}
                          >
                            {chartSong.song.title}
                            {isNewSong(chartSong.song.createdAt) && <span className="new-badge">NEW</span>}
                          </div>
                        </div>
                        <div className="chart-meta-row">
                          <div className="chart-artist">{userName}</div>
                          <div className="chart-actions">
                            <button
                              className="chart-edit-btn"
                              onClick={(e) => {
                                e.stopPropagation();
                                downloadSong(chartSong.song);
                              }}
                              title="Download song"
                            >
                              ⬇️
                            </button>
                            <button
                              className="chart-edit-btn"
                              onClick={(e) => {
                                e.stopPropagation();
                                startEditingChart(chartSong.song.id!, chartSong.song.title);
                              }}
                            >
                              ✏️
                            </button>
                            <div className="chart-indicator">🔥</div>
                          </div>
                        </div>
                      </div>
                    </>
                  ) : (
                    <div className="chart-empty">
                      <div className="empty-slot">Drop song here</div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Sticky Music Player */}
      {sortedSongs.length > 0 && (
        <div className="sticky-player">
          <div className="player-content">
            <div className="now-playing">
              {currentSong && currentGameData && (
                <>
                  <div className="player-album-art">
                    <img src={currentGameData.thumbnail} alt={currentGameData.title} />
                  </div>
                  <div className="player-info">
                    <div className="player-song-title">{currentSong.title}</div>
                    <div className="player-artist">{userName}</div>
                  </div>
                </>
              )}
            </div>

            <div className="player-controls">
              <button
                className="control-btn"
                onClick={() => {
                  setPlayQueueType('album');
                  const prevIndex = currentSongIndex > 0 ? currentSongIndex - 1 : sortedSongs.length - 1;
                  setCurrentSongIndex(prevIndex);
                  if (sortedSongs[prevIndex]) {
                    playRealAudio(sortedSongs[prevIndex]);
                  }
                }}
              >
                ⏮️
              </button>
              <button
                className="play-pause-btn"
                onClick={() => {
                  if (currentSong) {
                    if (isPlaying && currentlyPlaying === currentSong.id) {
                      currentAudio?.pause();
                      setIsPlaying(false);
                      setCurrentlyPlaying(null);
                    } else {
                      setPlayQueueType('album');
                      playRealAudio(currentSong);
                    }
                  }
                }}
              >
                {isPlaying && currentlyPlaying === currentSong?.id ? '⏸️' : '▶️'}
              </button>
              <button
                className="control-btn"
                onClick={() => {
                  setPlayQueueType('album');
                  const nextIndex = currentSongIndex < sortedSongs.length - 1 ? currentSongIndex + 1 : 0;
                  setCurrentSongIndex(nextIndex);
                  if (sortedSongs[nextIndex]) {
                    playRealAudio(sortedSongs[nextIndex]);
                  }
                }}
              >
                ⏭️
              </button>
            </div>

            <div className="player-progress">
              <div className="progress-bar">
                <div
                  className="progress-fill"
                  style={{ width: duration > 0 ? `${(currentTime / duration) * 100}%` : '0%' }}
                ></div>
              </div>
              <div className="time-info">
                <span>{formatTime(currentTime)}</span>
                <span>{formatTime(duration)}</span>
              </div>
            </div>

            <div className="player-volume">
              <button className="volume-btn">🔊</button>
              <input
                type="range"
                min="0"
                max="1"
                step="0.1"
                value={volume}
                onChange={(e) => {
                  const newVolume = parseFloat(e.target.value);
                  setVolume(newVolume);
                  if (currentAudio) {
                    currentAudio.volume = newVolume;
                  }
                }}
                className="volume-slider"
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Format time in mm:ss format
const formatTime = (timeInSeconds: number): string => {
  if (isNaN(timeInSeconds) || timeInSeconds < 0) return '0:00';
  const minutes = Math.floor(timeInSeconds / 60);
  const seconds = Math.floor(timeInSeconds % 60);
  return `${minutes}:${seconds.toString().padStart(2, '0')}`;
};

const getElementIcon = (element: string): string => {
  const iconMap: Record<string, string> = {
    'harmony': '🎵',
    'instruments': '🎸',
    'melody': '🎼',
    'form': '🏗️',
    'rhythm': '🥁',
    'dynamics': '📢'
  };
  return iconMap[element] || '🎶';
};

export default SavedSongs; 